/**
 * The supplier portal's wording table: every status, action and activity label a supplier reads, in one place
 * (brief §11: "supplier-facing words need a wording table, as the customer pages have, checked with suppliers").
 * Taken from the approved journeys (`docs/features/supplier-interface/01a_journeys_mockups.html`).
 *
 * The server decides (a line's status, what it allows, what the order needs next); this file only words it. A
 * service order is worded by its kind (brief decision 9): "Posted" with proof of posting for a mailing,
 * "Packed" for fulfilment (kitting included), "Installed" with sign-off, "Delivered" for design (Ben, 9 Oct),
 * "Decorated" for decoration (R145).
 *
 * Platform-neutral: strings and pure functions only.
 */

import { format, parseISO } from "date-fns";
import { currentTime } from "./current-time";
import type {
  Carrier,
  Delivery,
  InboundGoods,
  InvoiceGroup,
  InvoiceLedgerStatus,
  LineAction,
  NeedsYou,
  PostageAccount,
  AcceptedAtPrice,
  ProposedDateAnswer,
  ActivityEntry,
  PurchaseOrderEventSource,
  PurchaseOrderLine,
  PurchaseOrderListItem,
  PurchaseOrderPage,
  PurchaseOrderStatus,
  PurchaseOrderListTab,
  ServiceKind,
  ServiceRef,
  SupplierAccount,
  SupplierInvoice,
  SupplierKitDrop,
} from "../data/supplier-portal-adapter";
import type {
  GoodsReceivedResult,
  GoodsRefusalReason,
  SupplierGoodsInRow,
  SupplierGoodsInState,
  SupplierGoodsInTab,
} from "../data/supplier-goods-in-adapter";
import type {
  PickDeliverTo,
  PickDespatchResult,
  PickRefusalReason,
  SuggestedPickBatch,
  SupplierPickRow,
  SupplierPickState,
  SupplierPickTab,
} from "../data/supplier-picks-adapter";

/** The status pill's colour, by meaning: something to do, fine, a warning, a problem, in transit, finished. */
export type SupplierTone = "todo" | "ok" | "warn" | "bad" | "info" | "mute";

export interface Worded {
  label: string;
  tone: SupplierTone;
}

// ── Dates and amounts ──

/** "7 Oct". */
export function dayMonth(isoDate: string): string {
  return format(parseISO(isoDate), "d MMM");
}

/** The platform's today as "2026-10-13": the default for a date the server has not named (`currentTime()`, so a
 *  preview's fast-forwarded clock is followed). */
export function platformToday(): string {
  return format(currentTime(), "yyyy-MM-dd");
}

/** "8 Oct 09:14". */
export function dayMonthTime(isoDateTime: string): string {
  return format(parseISO(isoDateTime), "d MMM HH:mm");
}

const POUNDS = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

/** "£4,210.00" from the decimal string the ledger gives. */
export function pounds(decimal: string): string {
  return POUNDS.format(Number(decimal));
}

/** "10,450". */
export function count(n: number): string {
  return n.toLocaleString("en-GB");
}

function plural(n: number, one: string, many: string): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

/** "line 2", "lines 2 and 3", "lines 1, 2 and 3". */
export function lineList(lineNos: readonly number[]): string {
  if (lineNos.length === 1) return `line ${lineNos[0]}`;
  const head = lineNos.slice(0, -1).join(", ");
  return `lines ${head} and ${lineNos[lineNos.length - 1]}`;
}

// ── Service wording (brief decision 9) ──

interface ServiceWording {
  /** The past tense that replaces "despatched". */
  done: string;
  /** The action that replaces "Mark despatched". */
  action: string;
  /** "Post by", for the header and the Needs you box. */
  by: string;
  /** The file that goes with it, if any, and how the Needs you box asks for it. */
  evidence: string | null;
  evidenceAsk: string | null;
}

const ITEM_WORDING: ServiceWording = {
  done: "Despatched",
  action: "Mark despatched",
  by: "Despatch by",
  evidence: null,
  evidenceAsk: null,
};

// The dictionary's service kinds. Mailing and installation are the mock-ups' wording; the rest follow the same
// pattern for kinds the mock-ups don't show.
const SERVICE_WORDING: Record<ServiceKind, ServiceWording> = {
  mailing: {
    done: "Posted",
    action: "Mark posted",
    by: "Post by",
    evidence: "Proof of posting",
    evidenceAsk: "upload proof of posting",
  },
  postage: {
    done: "Posted",
    action: "Mark posted",
    by: "Post by",
    evidence: "Proof of posting",
    evidenceAsk: "upload proof of posting",
  },
  carriage: {
    done: "Delivered",
    action: "Mark delivered",
    by: "Deliver by",
    evidence: null,
    evidenceAsk: null,
  },
  fulfilment: {
    done: "Packed",
    action: "Mark packed",
    by: "Pack by",
    evidence: null,
    evidenceAsk: null,
  },
  storage: {
    done: "Stored",
    action: "Mark stored",
    by: "Store by",
    evidence: null,
    evidenceAsk: null,
  },
  design: {
    done: "Delivered",
    action: "Mark delivered",
    by: "Deliver by",
    evidence: null,
    evidenceAsk: null,
  },
  installation: {
    done: "Installed",
    action: "Mark installed",
    by: "Install by",
    evidence: "Sign-off",
    evidenceAsk: "upload the sign-off",
  },
  decoration: {
    done: "Decorated",
    action: "Mark decorated",
    by: "Decorate by",
    evidence: null,
    evidenceAsk: null,
  },
};

/** Kitting is an activity of fulfilment (the dictionary): a display label ("Kitting"); its done word is fulfilment's, "Packed". */
export function isKitting(service: ServiceRef | null): boolean {
  return (
    service?.kind === "fulfilment" &&
    service.activities.some((a) => a.code === "kitting")
  );
}

export function despatchWording(service: ServiceRef | null): ServiceWording {
  if (!service) return ITEM_WORDING;
  return SERVICE_WORDING[service.kind];
}

const SERVICE_NOUN: Record<ServiceKind, string> = {
  mailing: "Mailing",
  postage: "Postage",
  carriage: "Carriage",
  fulfilment: "Fulfilment",
  storage: "Storage",
  design: "Design",
  installation: "Installation",
  decoration: "Decoration",
};

export function serviceNoun(service: ServiceRef): string {
  return isKitting(service) ? "Kitting" : SERVICE_NOUN[service.kind];
}

const POSTAGE_ACCOUNT: Record<PostageAccount, string> = {
  supplier: "on your account",
  pollards: "on Pollards' account",
  customer: "on the customer's account",
};

export function postageAccountLabel(account: PostageAccount): string {
  return POSTAGE_ACCOUNT[account];
}

// ── Carriers ──

const CARRIER_LABEL: Record<Carrier, string> = {
  dpd: "DPD",
  dx: "DX",
  royal_mail: "Royal Mail",
  parcelforce: "Parcelforce",
  evri: "Evri",
  ups: "UPS",
  fedex: "FedEx",
  tnt: "TNT",
  dhl_express: "DHL Express",
  dhl_parcel_uk: "DHL Parcel UK",
  yodel: "Yodel",
  inpost: "InPost",
  apc_overnight: "APC Overnight",
  pall_ex: "Pall-Ex",
  palletforce: "Palletforce",
  palletways: "Palletways",
  the_pallet_network: "The Pallet Network",
  xdp: "XDP",
  own_van: "Own van",
  other: "Other",
};

export function carrierLabel(carrier: Carrier): string {
  return CARRIER_LABEL[carrier];
}

/** Carriers CIS records but cannot follow yet: their tracking needs the delivery postcode (a named follow-up). */
const RECORDED_NOT_TRACKED: readonly Carrier[] = [
  "palletways",
  "the_pallet_network",
  "xdp",
];

/** Whether CIS can follow delivery with this carrier from a tracking number. */
export function isTrackedCarrier(carrier: Carrier): boolean {
  return (
    carrier !== "own_van" &&
    carrier !== "other" &&
    !RECORDED_NOT_TRACKED.includes(carrier)
  );
}

/** "DPD 15501 2384 7698", "Own van", "Acme Haulage 123". */
export function carrierAndTracking(
  carrier: Carrier,
  carrierName: string | null,
  trackingNumber: string | null,
): string {
  const name =
    carrier === "other" && carrierName ? carrierName : carrierLabel(carrier);
  return trackingNumber ? `${name} ${trackingNumber}` : name;
}

// ── Statuses ──

const ORDER_STATUS: Record<
  Exclude<PurchaseOrderStatus, "closed" | "cancelled">,
  Worded
> = {
  to_accept: { label: "To accept", tone: "todo" },
  accepted: { label: "Accepted", tone: "ok" },
  despatched: { label: "Despatched", tone: "info" },
};

/** The order header's pill: "To accept", "Closed · delivered 2 Oct". */
export function orderStatus(
  page: Pick<PurchaseOrderPage, "status" | "closed_on" | "service">,
): Worded {
  if (page.status === "closed") {
    const finished = page.service
      ? despatchWording(page.service).done.toLowerCase()
      : "delivered";
    return {
      label: page.closed_on
        ? `Closed · ${finished} ${dayMonth(page.closed_on)}`
        : "Closed",
      tone: "mute",
    };
  }
  if (page.status === "cancelled") {
    return {
      label: page.closed_on
        ? `Cancelled ${dayMonth(page.closed_on)}`
        : "Cancelled",
      tone: "bad",
    };
  }
  if (page.status === "despatched") {
    return {
      label: despatchWording(page.service).done,
      tone: "info",
    };
  }
  return ORDER_STATUS[page.status];
}

/** A line's pill: "To accept", "Date proposed: 17 Oct", "Despatched · DPD 15501 2384 7698". */
export function lineStatus(
  line: Pick<
    PurchaseOrderLine,
    "status" | "proposed_date" | "deliveries" | "service"
  >,
): Worded {
  const done = despatchWording(line.service).done;
  const last = line.deliveries[line.deliveries.length - 1] as
    Delivery | undefined;
  switch (line.status) {
    case "to_accept":
      return { label: "To accept", tone: "todo" };
    case "accepted":
      return { label: "Accepted", tone: "ok" };
    case "price_to_approve":
      return { label: "Price waiting for approval", tone: "warn" };
    case "date_proposed":
      return {
        label: line.proposed_date
          ? `Date proposed: ${dayMonth(line.proposed_date)}`
          : "Date proposed",
        tone: "warn",
      };
    case "cancel_requested":
      return { label: "Cancellation requested", tone: "warn" };
    case "rejected":
      return { label: "Rejected", tone: "bad" };
    case "part_despatched":
      return { label: `Part ${done.toLowerCase()}`, tone: "info" };
    case "despatched":
      return {
        label:
          last?.carrier && last.tracking_number
            ? `${done} · ${carrierAndTracking(last.carrier, last.carrier_name, last.tracking_number)}`
            : done,
        tone: "info",
      };
    case "delivered":
      return {
        label: line.service ? done : "Delivered",
        tone: "ok",
      };
    case "cancelled":
      return { label: "Cancelled", tone: "mute" };
  }
}

/**
 * What the supplier reads under a despatched delivery: "✓ Despatched 13 Oct. Pollards will follow delivery with DPD."
 * Own van says when it is expected; an untracked carrier says nothing about following it.
 */
export function deliveryDoneSentence(
  delivery: Delivery,
  service: ServiceRef | null,
): string {
  const done = despatchWording(service).done;
  const when = `${done} ${dayMonth(delivery.despatched_on)}`;
  if (delivery.carrier === null) return `${when}.`;
  if (delivery.carrier === "own_van") {
    return delivery.deliver_on
      ? `${when}. Arriving by your own van ${dayMonth(delivery.deliver_on)}.`
      : `${when} by your own van.`;
  }
  if (isTrackedCarrier(delivery.carrier) && delivery.tracking_number) {
    return `${when}. Pollards will follow delivery with ${carrierLabel(delivery.carrier)}.`;
  }
  return `${when}.`;
}

/** The proof row: "Checked, approved 8 Oct". */
export function proofStatus(
  proof: NonNullable<PurchaseOrderLine["proof"]>,
): Worded {
  switch (proof.state) {
    case "to_upload":
      return { label: "Waiting for your proof", tone: "todo" };
    case "checking":
      return { label: "Uploaded, being checked", tone: "info" };
    case "approved":
      return {
        label: proof.decided_on
          ? `Checked, approved ${dayMonth(proof.decided_on)}`
          : "Checked, approved",
        tone: "ok",
      };
    case "changes_needed":
      return { label: "Changes needed", tone: "bad" };
  }
}

const INBOUND_STATUS: Record<
  Exclude<InboundGoods["status"], "despatched">,
  Worded
> = {
  in_production: { label: "In production", tone: "mute" },
  delivered: { label: "Delivered to you", tone: "ok" },
};

/** "Despatched · DX 4410…": the tracking number shortened, as the mock-up shows it. */
export function inboundStatus(goods: InboundGoods): Worded {
  if (goods.status !== "despatched") return INBOUND_STATUS[goods.status];
  if (!goods.carrier) return { label: "Despatched", tone: "info" };
  const tracking = goods.tracking_number
    ? ` ${goods.tracking_number.slice(0, 4)}…`
    : "";
  return {
    label: `Despatched · ${carrierLabel(goods.carrier)}${tracking}`,
    tone: "info",
  };
}

// ── Actions ──

/** A line action's label. `short` is the phone label where the mock-up shortens it ("Date"). */
export function lineActionLabel(
  action: LineAction,
  service: ServiceRef | null,
): { label: string; short: string } {
  switch (action) {
    case "accept":
      return { label: "Accept", short: "Accept" };
    case "accept_at_price":
      return { label: "Accept at a different price", short: "Different price" };
    case "propose_date":
      return { label: "Can't meet the date", short: "Date" };
    case "reject":
      return { label: "Reject", short: "Reject" };
    case "mark_despatched": {
      const label = despatchWording(service).action;
      return { label, short: label };
    }
    case "upload_proof":
      return { label: "Upload proof", short: "Upload proof" };
    case "answer_cancellation":
      return { label: "Confirm cancellation", short: "Confirm" };
    case "answer_amendment":
      return { label: "Accept the change", short: "Accept" };
  }
}

const PENCE_AS_POUNDS = (pence: number): string => POUNDS.format(pence / 100);

/**
 * What the supplier reads before it confirms a price of its own (brief 26 §8, provisional): the same sentence for every
 * increase, held or not, so the trigger is never revealed. Only the count changes. Null when the price does not raise
 * the cost we sent.
 */
export function priceIncreaseConfirmation(input: {
  purchaseOrderNo: string;
  fromPence: number;
  toPence: number;
  nextIncrease: string | null;
}): string | null {
  if (input.toPence <= input.fromPence) return null;
  const raises = `This raises the price of ${input.purchaseOrderNo} from ${PENCE_AS_POUNDS(input.fromPence)} to ${PENCE_AS_POUNDS(input.toPence)}.`;
  const count = input.nextIncrease
    ? ` This will be your ${input.nextIncrease} price increase this month.`
    : "";
  return `${raises}${count} Accurate quoting keeps our work together running smoothly, and our buying team reviews every price increase.`;
}

/**
 * What the portal says once the supplier has given a price of its own (provisional): accepted, or waiting for the buying
 * team. A held answer never says which rule held it.
 */
export function acceptedAtPriceSentence(
  purchaseOrderNo: string,
  answer: AcceptedAtPrice,
): string {
  return answer.outcome === "accepted"
    ? `Thank you. Your new price of ${PENCE_AS_POUNDS(answer.price_pence)} for ${purchaseOrderNo} is accepted. Please go ahead.`
    : `Thank you. Your new price for ${purchaseOrderNo} needs our buying team's approval. Please don't start work on the changed lines until we email you, usually within one working day.`;
}

/** What a line asked to be cancelled says (provisional wording): Pollards' request and the day to answer by. */
export function cancellationRequestedSentence(
  answerBy: string | null | undefined,
): string {
  return answerBy
    ? `Pollards has asked to cancel this line. Please answer by ${format(parseISO(answerBy), "d MMM")}.`
    : "Pollards has asked to cancel this line. Please answer.";
}

/** What a line Pollards changed after the supplier had the order says (provisional wording): the change and the day to answer by. */
export function changeToAnswerSentence(change: {
  changed_on: string;
  change: "deliveries";
  answer_by: string | null;
}): string {
  const changedOn = format(parseISO(change.changed_on), "d MMM");
  const what = "the deliveries";
  return change.answer_by
    ? `Changed on ${changedOn}: ${what}. Please accept the change by ${format(parseISO(change.answer_by), "d MMM")}.`
    : `Changed on ${changedOn}: ${what}. Please accept the change.`;
}

/**
 * Pounds typed into the costs box, as whole pence: "12.50" is 1250, "" is not a number. Null when it is not an amount of
 * money (more than two decimals, a letter, a minus).
 */
export function poundsToPence(text: string): number | null {
  const cleaned = text.trim().replace(/^£/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [pounds, pence = ""] = cleaned.split(".");
  return Number(pounds) * 100 + Number(pence.padEnd(2, "0"));
}

/**
 * What the portal says once the supplier has proposed a later date (provisional wording): Pollards accepted it at
 * once because it still meets the customer's date, or a person at Pollards will decide.
 */
export function proposedDateAnswerSentence(answer: ProposedDateAnswer): string {
  return answer.accepted_date
    ? `Thanks. Your new date of ${format(parseISO(answer.accepted_date), "d MMM yyyy")} is accepted.`
    : "Thanks. Pollards will let you know about your new date.";
}

export const ACTION_WORDING = {
  acceptAll: "Accept all",
  acceptAtPrice: "Accept at this price",
  reviewLines: "Review lines ↓",
  sendToPollards: "Send to Pollards",
  cancel: "Cancel",
  partDelivery: "Part delivery…",
  wholeLine: "Whole line",
  uploadInvoice: "Upload invoice",
  sendInvoice: "Send invoice",
  sendReply: "Send reply",
  attachFile: "Attach a file",
  reportShortage: "Report a shortage on arrival",
  signIn: "Sign in",
  signOut: "Sign out",
  download: "Download",
  downloadAll: "Download all",
  open: "Open",
  rejectLine: "Reject line",
  confirmCancellation: "Confirm cancellation",
  cannotCancel: "We can't cancel this",
  acceptChange: "Accept the change",
  cannotMakeChange: "We can't make this change",
  uploadProof: "Upload proof",
  recordDespatch: "Record despatch",
  recordReceived: "Record received",
} as const;

/** The forms' labels. Service lines word their date by kind ("Posted on"). */
export const FORM_WORDING = {
  acceptAtPriceTitle: "Accept at a different price",
  acceptAtPriceLabel: "Your price for the whole line (ex VAT)",
  acceptAtPriceHint: "Your delivered price, in pounds.",
  confirmCancellationTitle: "Confirm the cancellation",
  cancellationCosts: "Costs you've already incurred (ex VAT)",
  cancellationCostsHint: "Enter £0.00 if there are none.",
  cannotCancelTitle: "We can't cancel this",
  cannotCancelReason: "Why you can't cancel it",
  cannotMakeChangeTitle: "We can't make this change",
  cannotMakeChangeReason: "Why you can't make the change",
  cantMeetTitle: "Can't meet the date",
  earliestDate: "Earliest you can deliver",
  reasonOptional: "Reason (optional)",
  rejectTitle: "Reject this line",
  rejectReason: "Why can't you take it?",
  rejectHint: "Pollards will choose another supplier for this line.",
  quantity: "Quantity in this delivery",
  carrier: "Carrier",
  chooseCarrier: "Choose a carrier",
  trackingNumber: "Tracking number",
  carrierName: "Carrier's name",
  deliveryDate: "Delivery date",
  carriersHint:
    "Carriers: DPD, DX, Royal Mail, Parcelforce, Evri, UPS and the other couriers listed, own van, other. Own van asks for the delivery date instead of tracking.",
  stillToSend: (n: number) => `${count(n)} still to send after this`,
  proofTitle: "Upload a proof",
  proofHint:
    "A PDF, PNG or JPEG. Pollards checks it against the print-ready file.",
  shortageTitle: "Report a shortage on arrival",
  shortageWhat: "What arrived short",
  shortageReceived: "How many arrived",
  shortageNote: "Anything else (optional)",
  shortageSent: "Thanks. Pollards will chase the shortfall.",
} as const;

/** "Despatched on", "Posted on", "Packed on", "Installed on". */
export function despatchDateLabel(service: ServiceRef | null): string {
  return `${despatchWording(service).done} on`;
}

// ── Needs you ──

export interface NeedsYouWording {
  headline: string;
  detail: string | null;
}

/** The "Needs you" box: "Accept or reject 2 lines by 9 Oct" / "1 line already accepted". */
export function needsYouWording(
  needs: NeedsYou,
  service: ServiceRef | null,
): NeedsYouWording {
  switch (needs.kind) {
    case "accept_lines":
      return {
        headline: `Accept or reject ${plural(needs.line_count, "line", "lines")}${needs.by ? ` by ${dayMonth(needs.by)}` : ""}`,
        detail:
          needs.already_accepted_count > 0
            ? `${plural(needs.already_accepted_count, "line", "lines")} already accepted`
            : null,
      };
    case "despatch": {
      if (service) {
        const w = despatchWording(service);
        return {
          headline: `${needs.by ? `${w.by} ${dayMonth(needs.by)}` : w.by}${w.evidenceAsk ? ` and ${w.evidenceAsk}` : ""}`,
          detail: needs.goods_arriving_on
            ? `Goods arriving ${dayMonth(needs.goods_arriving_on)}`
            : null,
        };
      }
      return {
        headline: needs.by
          ? `Despatch by ${dayMonth(needs.by)} and add the tracking number`
          : "Despatch and add the tracking number",
        detail: `${plural(needs.line_count, "line", "lines")} to despatch`,
      };
    }
    case "awaiting_pollards":
      return {
        headline: "Pollards is looking at the date you proposed",
        detail: `${plural(needs.line_count, "line", "lines")} waiting on Pollards. We'll email you when it's decided.`,
      };
    case "upload_invoice":
      return {
        headline: "Upload your invoice",
        detail: needs.until
          ? `You can upload it until ${dayMonth(needs.until)}`
          : null,
      };
  }
}

// ── The page's banners and empty sentences ──

export const PAGE_WORDING = {
  brand: "Pollards",
  product: "Supplier portal",
  linkScope: (purchaseOrderNo: string) =>
    `You're viewing purchase order ${purchaseOrderNo} only. Sign in to see all your orders and invoices.`,
  invoiceNotYet: (service: ServiceRef | null) =>
    `You can upload your invoice once a line is ${despatchWording(service).done.toLowerCase()}.`,
  closedBanner: (until: string) =>
    `This order is finished. You can still upload an invoice for it until ${dayMonth(until)}.`,
  closedNoInvoice: "This order is finished.",
  cancelledNoInvoice: "Nothing to invoice on this order.",
  downloadFailed: (fileCount: number) =>
    fileCount === 1
      ? "We couldn't download this file. Please try again, or ask your Pollards contact to email it."
      : "We couldn't download every file. Please try again, or ask your Pollards contact to email them.",
  cancelledBanner:
    "Pollards cancelled this order. Please don't make or send anything for it. If you've already started, reply to the order email.",
  linkNotValidTitle: "This link no longer works",
  linkNotValidWhy: "It may have expired, or the order may be finished.",
  linkNotValidNext:
    "Sign in to see your orders, or ask your Pollards contact for a new link.",
  notFoundTitle: "We can't find this purchase order",
  notFoundWhy:
    "It isn't one of your orders, or the number isn't right. Check the number on the order email.",
  accountManaged: (contactName: string) =>
    `To add or remove someone, ask ${contactName} in Pollards purchasing.`,
} as const;

/**
 * The staff view of the supplier portal (docs/features/supplier-portal-view-as/05 §6.5): where the supplier's page
 * would let the supplier change something, staff see the control drawn and switched off, and the page says why
 * once, in one note near the first of them (Ben's wording rule, "one thing once"). Each note opens with the same
 * first sentence and adds what only the supplier can do on that page. Provisional wording (docs/ui-wording.md);
 * the banner's wording is in the shell, beside the banner.
 */
const STAFF_VIEW_LOOKING =
  "You're looking at this page as the supplier sees it.";

export const STAFF_VIEW_WORDING = {
  order: `${STAFF_VIEW_LOOKING} Only the supplier can make changes to this order.`,
  invoices: `${STAFF_VIEW_LOOKING} Only the supplier can upload an invoice or reply to a query.`,
  quoteRequest: `${STAFF_VIEW_LOOKING} Only the supplier can send prices or decline the quote request.`,
  pickDespatch: `${STAFF_VIEW_LOOKING} Only the supplier can book a courier or record the despatch.`,
  picksDespatch: `${STAFF_VIEW_LOOKING} Only the supplier can record a despatch.`,
  goodsReceived: `${STAFF_VIEW_LOOKING} Only the supplier can record goods received.`,
  /**
   * Why a page is not found, for staff: they did not come from an email, so they are told whose pages the portal is
   * showing. `what` is "order", "pick" or "quote request"; the name is null before the staff session is read.
   */
  notFound: (
    what: "order" | "pick" | "quote request",
    supplierName: string | null,
  ) =>
    `This ${what} isn't one of ${supplierName ? `${supplierName}'s` : "this supplier's"}, or it doesn't exist.`,
  /** The person slot in the account menu: a staff view has no person. */
  menuPerson: "Staff view",
  /** The menu's last item, where the supplier's reads Sign out. */
  menuExit: "Exit staff view",
} as const;

/** A bought kit's line (V1 completion R5): where each pack goes, and the manifest of what it holds. */
export const KIT_WORDING = {
  heading: "Where each pack goes",
  summary: (kits: number, drops: number) =>
    `${count(drops)} ${drops === 1 ? "drop" : "drops"} · ${count(kits)} ${kits === 1 ? "pack" : "packs"}`,
  show: "Show where each pack goes",
  hide: "Hide the list",
  manifest: "Download the manifest (CSV)",
  manifestHint: "Every place against what each pack holds.",
  search: "Search places or postcodes",
  loading: "Loading the places…",
  noMatch: "No place matches that search.",
  noDrops: "There are no places yet.",
  failed: "We couldn't load the places. Please try again.",
  rows: (first: number, last: number, total: number) =>
    `Places ${count(first)}–${count(last)} of ${count(total)}`,
  columns: {
    drop: "Drop",
    place: "Place",
    postcode: "Postcode",
    packs: "Packs",
    status: "Status",
  },
} as const;

/** A drop's status as a pill: to despatch is the supplier's to do, despatched is on its way. */
export function kitDropTone(status: SupplierKitDrop["status"]): SupplierTone {
  switch (status) {
    case "to_despatch":
      return "todo";
    case "despatched":
      return "info";
    case "delivered":
      return "ok";
    case "cancelled":
      return "mute";
  }
}

/** The header's line under the order number. */
export function orderMeta(page: PurchaseOrderPage): string {
  const parts = [page.supplier_name];
  parts.push(`raised ${dayMonth(page.raised_on)}`);
  const by = page.service
    ? despatchWording(page.service).by.toLowerCase()
    : "ready by";
  if (page.ready_by) parts.push(`${by} ${dayMonth(page.ready_by)}`);
  parts.push(
    `your contact at Pollards: ${page.pollards_contact.name}, ${page.pollards_contact.role}`,
  );
  return parts.join(" · ");
}

/** Who placed the order: "Placed by BCQ Solutions Ltd" (the trading company's legal name); null when One cannot say. */
export function placedByLine(page: PurchaseOrderPage): string | null {
  return page.placed_by ? `Placed by ${page.placed_by.legal_name}` : null;
}

/** The phone's shorter version: "Ready by 14 Oct · Sam R, Pollards". */
export function orderMetaShort(page: PurchaseOrderPage): string {
  const by = page.service ? despatchWording(page.service).by : "Ready by";
  const contact = `${page.pollards_contact.name}, Pollards`;
  return page.ready_by
    ? `${by} ${dayMonth(page.ready_by)} · ${contact}`
    : contact;
}

/** A line's heading: "Line 2 · Counter board 400×841, 3mm foamex · 6 versions". */
export function lineHeading(line: PurchaseOrderLine): string {
  const noun = line.service
    ? `${serviceNoun(line.service)}: ${line.description}`
    : line.description;
  const versions =
    line.versions.length > 1 ? ` · ${line.versions.length} versions` : "";
  return `Line ${line.line_no} · ${noun}${versions}`;
}

/** The quantity beside a line's heading: "220", "640 in total", "10,450 pieces". */
export function lineQuantity(line: PurchaseOrderLine): string {
  if (line.quantity_unit)
    return `${count(line.quantity)} ${line.quantity_unit}`;
  if (line.versions.length > 1) return `${count(line.quantity)} in total`;
  return count(line.quantity);
}

/** "V1 Kids Eat Free 120 · V2 Breakfast 110". */
export function versionsSummary(line: PurchaseOrderLine): string {
  return line.versions
    .map((v, i) => `V${i + 1} ${v.name} ${count(v.quantity)}`)
    .join(" · ");
}

/** "Foamex 5mm A0 easel · your ref SS-4471". */
export function supplierProductLabel(
  product: NonNullable<PurchaseOrderLine["supplier_product"]>,
): string {
  return product.reference
    ? `${product.name} · your ref ${product.reference}`
    : product.name;
}

/**
 * Where a line goes: "Tesco Family Dining, distribution hub, Milton Keynes · by 14 Oct", or "Same as line 1 · by
 * 14 Oct" when an earlier line goes to the same address.
 */
export function deliverToLabel(
  line: PurchaseOrderLine,
  lines: readonly PurchaseOrderLine[],
): string {
  const earlier = lines.find(
    (other) =>
      other.line_no < line.line_no &&
      other.deliver_to.address === line.deliver_to.address,
  );
  const where = earlier
    ? `Same as line ${earlier.line_no}`
    : line.deliver_to.address;
  return line.deliver_to.deliver_by
    ? `${where} · by ${dayMonth(line.deliver_to.deliver_by)}`
    : where;
}

/** "Machine enclose (C5, 3 items) · Mailsort · No polywrap". */
export function activitiesSummary(
  activities: NonNullable<PurchaseOrderLine["service"]>["activities"],
): string {
  return activities
    .map((a) => (a.detail ? `${a.name} (${a.detail})` : a.name))
    .join(" · ");
}

// ── Activity ──

const SOURCE_TAG: Record<PurchaseOrderEventSource, string> = {
  manual: "Pollards",
  link: "link",
  portal: "portal",
  file: "file",
  api: "API",
  email: "email",
  agent: "auto",
  carrier: "carrier",
  system: "auto",
};

/** The small tag after an activity entry saying how it arrived; null where the mock-up shows none. */
export function activitySourceTag(event: ActivityEntry): string | null {
  if (event.kind === "opened") return null;
  if (event.kind === "sent") return event.sent_via ?? null;
  return SOURCE_TAG[event.source];
}

/** One activity entry, worded for the supplier: "Jo B accepted line 1". */
export function activitySentence(
  event: ActivityEntry,
  service: ServiceRef | null,
): string {
  const who = event.actor_name ?? "Pollards";
  const lines = event.line_nos.length ? lineList(event.line_nos) : "the order";
  switch (event.kind) {
    case "sent":
      return "Pollards sent this order";
    case "opened":
      return `${who} opened it`;
    case "line_acknowledged":
      return `${who} accepted ${lines}`;
    case "line_rejected":
      return event.reason
        ? `${who} rejected ${lines}: ${event.reason}`
        : `${who} rejected ${lines}`;
    case "date_change_proposed": {
      const date = event.date ? ` ${dayMonth(event.date)}` : " a later date";
      return `${who} proposed${date} for ${lines}${event.reason ? `: ${event.reason}` : ""}`;
    }
    case "date_change_acceptance":
      return event.outcome === "accepted"
        ? `Pollards accepted${event.date ? ` ${dayMonth(event.date)}` : " the new date"} for ${lines}`
        : `Pollards kept the original date for ${lines}`;
    case "despatched": {
      const done = despatchWording(service).done.toLowerCase();
      const how = event.carrier
        ? `, ${carrierAndTracking(event.carrier, event.carrier_name, event.tracking_number)}`
        : "";
      return `${who} marked ${lines} ${done}${how}`;
    }
    case "delivered":
      return `${capitalise(lines)} delivered`;
    case "proof_submitted":
      return `${who} uploaded a proof for ${lines}`;
    case "proof_decided":
      if (event.outcome === "changes_needed") {
        return `Pollards asked for changes to the proof for ${lines}`;
      }
      return event.source === "agent" || event.source === "system"
        ? `Pollards' check passed the proof for ${lines}`
        : `Pollards approved the proof for ${lines}`;
    case "invoice_uploaded":
      return `${who} uploaded invoice ${event.invoice_ref ?? ""}`.trimEnd();
    case "shortage_reported":
      return `${who} reported a shortage on arrival${event.reason ? `: ${event.reason}` : ""}`;
    case "cancelled":
      return "Pollards cancelled this order";
    case "line_cancelled":
      return `Pollards cancelled ${lines}`;
    case "line_destinations_changed":
      return "Delivery addresses changed.";
  }
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ── Orders list ──

const TAB_LABEL: Record<PurchaseOrderListTab, string> = {
  to_accept: "To accept",
  in_production: "In production",
  despatched: "Despatched",
  closed: "Closed",
};

export function purchaseOrderTabLabel(tab: PurchaseOrderListTab): string {
  return TAB_LABEL[tab];
}

/** Tabs whose count is "waiting on you" (pink) rather than just a tally (grey); Closed shows no count. */
export function tabCountTone(
  tab: PurchaseOrderListTab,
): "todo" | "mute" | null {
  if (tab === "to_accept") return "todo";
  if (tab === "closed") return null;
  return "mute";
}

/**
 * A list row's pill: "Cancellation requested", "2 to accept", "Overdue 3 days", "Closed · delivered 2 Oct". A
 * cancellation waiting for the supplier's answer leads, as on the line (see lineStatus).
 */
export function listItemStatus(item: PurchaseOrderListItem): Worded {
  if (item.lines_cancel_requested > 0) {
    return { label: "Cancellation requested", tone: "warn" };
  }
  if (item.overdue_days !== null && item.overdue_days > 0) {
    return {
      label: `Overdue ${plural(item.overdue_days, "day", "days")}`,
      tone: "bad",
    };
  }
  if (item.status === "to_accept") {
    return item.lines_to_accept < item.line_count
      ? { label: `${count(item.lines_to_accept)} to accept`, tone: "todo" }
      : { label: "To accept", tone: "todo" };
  }
  if (item.status === "accepted")
    return { label: "In production", tone: "mute" };
  return orderStatus({
    status: item.status,
    closed_on: item.closed_on,
    service: null,
  });
}

/** "3 lines · Foamex easels, counter boards, strut cards". */
export function listItemWhat(item: PurchaseOrderListItem): string {
  return `${plural(item.line_count, "line", "lines")} · ${item.summary}`;
}

export const LIST_WORDING = {
  searchPlaceholder: "Search PO or product",
  needingAttention: "In production, needing attention",
  empty: (tab: PurchaseOrderListTab) =>
    tab === "to_accept"
      ? "Nothing to accept. New orders from Pollards appear here."
      : `No ${TAB_LABEL[tab].toLowerCase()} orders.`,
  noMatch: (q: string) => `No orders match "${q}".`,
} as const;

// ── Invoices ──

/** The ledger status pill: "Approved · due 30 Nov", "Paid · 2 Oct", "Query from Pollards". */
export function invoiceStatus(
  invoice: Pick<SupplierInvoice, "ledger_status" | "due_on" | "paid_on">,
  { short = false }: { short?: boolean } = {},
): Worded {
  switch (invoice.ledger_status as InvoiceLedgerStatus) {
    case "received":
      return { label: "Received", tone: "mute" };
    case "approved":
      return {
        label: invoice.due_on
          ? `Approved · due ${dayMonth(invoice.due_on)}`
          : "Approved",
        tone: "ok",
      };
    case "paid":
      return {
        label: invoice.paid_on ? `Paid · ${dayMonth(invoice.paid_on)}` : "Paid",
        tone: "ok",
      };
    case "query":
      return { label: short ? "Query" : "Query from Pollards", tone: "warn" };
  }
}

const INVOICE_GROUP_LABEL: Record<InvoiceGroup, string> = {
  in_progress: "In progress",
  paid: "Paid",
};

export function invoiceGroupLabel(group: InvoiceGroup): string {
  return INVOICE_GROUP_LABEL[group];
}

export const INVOICE_WORDING = {
  uploadTitle: "Upload an invoice",
  drop: "Drop a PDF here, or",
  chooseFile: "Choose file",
  forPurchaseOrders: "For purchase order(s)",
  invoiceNumber: "Your invoice number",
  invoiceNumberPlaceholder: "e.g. INV-9944",
  total: "Total inc VAT",
  invoiceDate: "Invoice date",
  yourReply: "Your reply",
  sent: "Thanks. Your invoice is with Pollards' finance team.",
  replied: "Reply sent to Pollards' finance team.",
  empty: (group: InvoiceGroup) =>
    group === "paid" ? "No paid invoices yet." : "No invoices in progress.",
  noPurchaseOrder: "—",
} as const;

// ── Account ──

export function ordersReachYou(
  route: SupplierAccount["orders_reach_you"],
): string {
  if (route === null)
    return "Not through the portal yet: ask your Pollards contact how orders reach you";
  switch (route.channel) {
    case "email_with_link":
      return `By email with a link, to ${route.address}`;
  }
}

// ── Warehouse picks (spec 14) ──
//
// A pick is goods leaving a warehouse for a customer, so its done word is the print and goods one, "Despatched"
// (Ben, 9 Oct). The pick id is internal and never worded: the line reference is what the warehouse quotes to us.

const PICK_STATE: Record<SupplierPickState, Worded> = {
  to_pick: { label: "To pick", tone: "todo" },
  despatched: { label: "Despatched", tone: "ok" },
  cancelled: { label: "Cancelled", tone: "bad" },
};

/** A pick's pill; a pick part-sent to its addresses reads "Part despatched". */
export function pickStatus(pick: {
  state: SupplierPickState;
  deliver_to: Pick<PickDeliverTo, "despatched_quantity">[];
}): Worded {
  if (
    pick.state === "to_pick" &&
    pick.deliver_to.some((d) => d.despatched_quantity > 0)
  ) {
    return { label: "Part despatched", tone: "info" };
  }
  return PICK_STATE[pick.state];
}

const PICK_TAB_LABEL: Record<SupplierPickTab, string> = {
  to_pick: "To pick",
  despatched_today: "Despatched today",
  all: "All",
};

export function pickTabLabel(tab: SupplierPickTab): string {
  return PICK_TAB_LABEL[tab];
}

/** To pick is what is waiting on the supplier (pink); the other tabs are tallies. */
export function pickTabCountTone(tab: SupplierPickTab): "todo" | "mute" {
  return tab === "to_pick" ? "todo" : "mute";
}

/** Why one despatch was refused, as a sentence. Batch trouble is staff's to sort out and they are told at once. */
const PICK_REFUSAL: Record<PickRefusalReason, string> = {
  not_found: "We can't find this pick at your warehouse.",
  despatched_under_another_ref:
    "This pick has already been despatched under a different reference.",
  despatch_ref_reused:
    "That reference is already used for another part of this pick. Use a new one.",
  pick_cancelled:
    "Pollards cancelled this pick. Please don't send it; leave the goods on the shelf.",
  not_picked:
    "Pollards hasn't released this pick from stock yet. Please try again later.",
  batch_refused:
    "Stock won't take that batch, so nothing was recorded. Pollards has been told and will sort it out; please leave the goods where they are for now.",
  needs_delivery_line:
    "This pick goes to more than one address. Open it and record each address from its page.",
  nothing_left_to_send: "Everything for that address has already been sent.",
  quantity_exceeds_remaining:
    "That's more than is still to go to that address.",
  waiting_for_stock:
    "The rest of this pick is waiting for stock. Open the pick to see how many to send now.",
  not_recorded:
    "We couldn't record this one. Please try again, or tell your Pollards contact.",
};

export function pickRefusalSentence(reason: PickRefusalReason | null): string {
  return reason ? PICK_REFUSAL[reason] : PICK_REFUSAL.not_recorded;
}

/** One result's label: "Recorded", "Already recorded", or the refusal's sentence. */
export function pickResultSentence(result: PickDespatchResult): string {
  if (result.outcome === "recorded") return "Recorded";
  if (result.outcome === "already_recorded") return "Already recorded";
  return pickRefusalSentence(result.reason_code);
}

/** "2 recorded, 1 already recorded, 1 refused" for the banner over a bulk result. */
export function pickResultsSummary(results: PickDespatchResult[]): string {
  const of = (outcome: PickDespatchResult["outcome"]) =>
    results.filter((r) => r.outcome === outcome).length;
  const tallies: [number, string][] = [
    [of("recorded"), "recorded"],
    [of("already_recorded"), "already recorded"],
    [of("refused"), "refused"],
  ];
  return tallies
    .filter(([n]) => n !== 0)
    .map(([n, what]) => `${count(n)} ${what}`)
    .join(", ");
}

/** What a batch is called on the shelf: its goods-in reference when it has one, else its number. */
export function batchName(batch: SuggestedPickBatch): string {
  return `batch ${batch.goods_in_ref ?? batch.batch_no}`;
}

/** "Take batch 14, received 2 Sep, artwork rev C"; "Take 600 from batch …" when one batch won't cover the pick. */
export function batchInstruction(
  batch: SuggestedPickBatch,
  pickQuantity: number,
  { then = false }: { then?: boolean } = {},
): string {
  const lead =
    batch.quantity < pickQuantity
      ? `${then ? "then take" : "Take"} ${count(batch.quantity)} from ${batchName(batch)}`
      : `${then ? "or take" : "Take"} ${batchName(batch)}`;
  const parts = [lead, `received ${dayMonth(batch.received_on)}`];
  if (batch.artwork_revision_id)
    parts.push(`artwork rev ${batch.artwork_revision_id}`);
  return parts.join(", ");
}

/** What to watch for on a batch, to show beside its instruction: "Older artwork" (not the item's current revision, so
 *  stale print) and "Valid until 31 Dec 2026" (its own last valid day). Empty for an ordinary batch. */
export function batchNotes(batch: SuggestedPickBatch): string[] {
  const notes: string[] = [];
  if (batch.is_older_artwork) notes.push(PICK_WORDING.olderArtwork);
  if (batch.valid_until)
    notes.push(
      `${PICK_WORDING.validUntil} ${format(parseISO(batch.valid_until), "d MMM yyyy")}`,
    );
  return notes;
}

export interface BatchLine {
  text: string;
  notes: string[];
}

/** The batch to take, then the others listed after it; null for an untracked item. */
export function batchInstructions(
  batches: readonly SuggestedPickBatch[],
  pickQuantity: number,
): { take: BatchLine; others: BatchLine[] } | null {
  const [first, ...rest] = batches;
  if (!first) return null;
  return {
    take: {
      text: batchInstruction(first, pickQuantity),
      notes: batchNotes(first),
    },
    others: rest.map((b) => ({
      text: batchInstruction(b, pickQuantity, { then: true }),
      notes: batchNotes(b),
    })),
  };
}

/** "Halden Garden Centres, Reading · RG2 6AB". */
export function pickDestinationLabel(
  destination: Pick<PickDeliverTo, "address_name" | "postcode">,
): string {
  return [destination.address_name, destination.postcode]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

/** The list's Deliver to cell: the one address, or "2 addresses". */
export function pickDeliverToSummary(
  deliverTo: readonly Pick<PickDeliverTo, "address_name" | "postcode">[],
): string {
  return deliverTo.length === 1
    ? pickDestinationLabel(deliverTo[0])
    : plural(deliverTo.length, "address", "addresses");
}

/** What is still to go to an address. */
export function stillToGo(
  destination: Pick<PickDeliverTo, "quantity" | "despatched_quantity">,
): number {
  return Math.max(0, destination.quantity - destination.despatched_quantity);
}

/**
 * What the warehouse can send now: what the pick holds that has not gone. A line stock holds only in part (P22) is
 * picked in parts as stock arrives, so this can be less than what is still to go to its addresses.
 */
export function pickToSendNow(pick: {
  quantity: number;
  deliver_to: readonly Pick<PickDeliverTo, "despatched_quantity">[];
}): number {
  const sent = pick.deliver_to.reduce(
    (sum, d) => sum + d.despatched_quantity,
    0,
  );
  return Math.max(0, pick.quantity - sent);
}

/** What can go to an address now: what is still to go there, but never more than the pick holds that has not gone. */
export function sendableTo(
  pick: Parameters<typeof pickToSendNow>[0],
  destination: Pick<PickDeliverTo, "quantity" | "despatched_quantity">,
): number {
  return Math.min(stillToGo(destination), pickToSendNow(pick));
}

/** "120 of 300 sent", "All 300 sent"; null for an address nothing has gone to. */
export function destinationProgress(
  destination: Pick<PickDeliverTo, "quantity" | "despatched_quantity">,
): string | null {
  if (destination.despatched_quantity <= 0) return null;
  return stillToGo(destination) === 0
    ? `All ${count(destination.quantity)} sent`
    : `${count(destination.despatched_quantity)} of ${count(destination.quantity)} sent`;
}

export function pickRowItem(
  row: Pick<SupplierPickRow, "stock_code" | "description">,
): string {
  return `${row.stock_code} · ${row.description}`;
}

export const PICK_WORDING = {
  searchPlaceholder: "Search reference, item or postcode",
  warehouse: "Warehouse",
  empty: (tab: SupplierPickTab, warehouseName: string | null) => {
    const at = warehouseName ? ` at ${warehouseName}` : "";
    switch (tab) {
      case "to_pick":
        return `Nothing to pick${at}. New picks from Pollards appear here.`;
      case "despatched_today":
        return `Nothing has been despatched today${at}.`;
      case "all":
        return `No picks${at} yet.`;
    }
  },
  noMatch: (q: string) => `No picks match "${q}".`,
  signedOutTitle: "You've been signed out",
  noWarehouses: "There are no warehouse picks for your account",
  noWarehousesWhy:
    "Your supplier doesn't run a warehouse for Pollards. If it should, ask your Pollards contact.",
  sortLabel: "Sort",
  sortOldest: "Oldest first",
  sortRequiredSoonest: "Required soonest",
  sortRequiredLatest: "Required latest",
  selectRow: (lineRef: string) => `Select ${lineRef}`,
  selectedCount: (n: number) => `${count(n)} selected`,
  bulkTitle: "Record despatch for the selected picks",
  bulkHint:
    "One reference, date and carrier for them all, as for one parcel or despatch note. Each pick is recorded on its own, so one being refused doesn't stop the rest.",
  needsAddress: "More than one address: open the pick",
  cancelledBanner:
    "Pollards cancelled this pick. Please don't send it; leave the goods on the shelf.",
  despatchedBanner: "This pick is despatched.",
  notFoundTitle: "We can't find this pick",
  notFoundWhy:
    "It isn't at one of your warehouses, or the link isn't right. Go back to the list and open it from there.",
  despatchRef: "Despatch reference",
  despatchRefHint: "Your despatch note or parcel reference.",
  despatchedOn: "Despatched on",
  destination: "Sent to",
  quantity: "Quantity sent",
  batchPicked: "Batch picked",
  suggestedBatch: "suggested",
  recordTitle: "Record despatch",
  resultsTitle: "Result",
  deliverTo: "Deliver to",
  despatchesSoFar: "Despatched so far",
  packingNote: "Packing note",
  requiredBy: "Required by",
  customerReference: "Customer reference",
  ourReference: "Our reference",
  batchToTake: "Batch to take",
  olderArtwork: "Older artwork",
  validUntil: "Valid until",
} as const;

// ── Booking a pick with the courier (courier chain) ──
//
// The courier's own names (DPD, DX) and its services come from the server; these are the panel's own words.

export const COURIER_WORDING = {
  title: "Book with a courier",
  hint: "Book DPD or DX from here and print the label. The despatch is recorded under the courier's number.",
  open: "Book with DPD or DX",
  courier: "Courier",
  chooseCourier: "Choose a courier",
  service: "Service",
  chooseService: "Choose a service",
  contents: "Contents on the label",
  parcels: "Parcels",
  parcelsHint: "(weight in kg, from the stock item; correct as packed)",
  addAParcelFirst: "Add at least one parcel.",
  parcel: (n: number) => `Parcel ${n}`,
  parcelWeight: (n: number) => `Parcel ${n} weight in kg`,
  removeParcel: (n: number) => `Remove parcel ${n}`,
  remove: "Remove",
  addParcel: "Add a parcel",
  couldNotAsk: "The courier could not be asked just now.",
  book: (courier: string | null) =>
    courier ? `Book with ${courier} and print label` : "Book and print label",
  booked: (courier: string, number: string, isRepeat: boolean) =>
    isRepeat
      ? `This was already booked with ${courier}: ${number}.`
      : `Booked with ${courier}: ${number}. The despatch is recorded under this number.`,
  labelFailed: "The label could not be opened. Print it again below.",
  printAgain: "Print label again",
} as const;

// ── Goods in at a warehouse (spec 22) ──
//
// Goods in is goods arriving at the warehouse for us, so its words are the code's own ("goods in", "received"; Ben,
// 21 Oct). The delivery line id is internal and never worded: the line reference is what the warehouse quotes to us.

const GOODS_IN_STATE: Record<SupplierGoodsInState, Worded> = {
  due: { label: "Due in", tone: "todo" },
  part_received: { label: "Part received", tone: "info" },
  received: { label: "Received", tone: "ok" },
  cancelled: { label: "Cancelled", tone: "bad" },
};

export function goodsInStatus(row: Pick<SupplierGoodsInRow, "state">): Worded {
  return GOODS_IN_STATE[row.state];
}

const GOODS_IN_TAB_LABEL: Record<SupplierGoodsInTab, string> = {
  due: "Due in",
  received_today: "Received today",
  all: "All",
};

export function goodsInTabLabel(tab: SupplierGoodsInTab): string {
  return GOODS_IN_TAB_LABEL[tab];
}

/** Due in is what is waiting on the supplier (pink); the other tabs are tallies. */
export function goodsInTabCountTone(tab: SupplierGoodsInTab): "todo" | "mute" {
  return tab === "due" ? "todo" : "mute";
}

/** How much of a delivery is in: "200 of 500 in" once any has arrived, else the quantity due. */
export function goodsInQuantity(
  row: Pick<SupplierGoodsInRow, "quantity" | "received_quantity">,
): string {
  return row.received_quantity > 0
    ? `${count(row.received_quantity)} of ${count(row.quantity)} in`
    : count(row.quantity);
}

/** What is still due on a delivery: the quantity a receipt starts at. */
export function goodsInOutstanding(
  row: Pick<SupplierGoodsInRow, "quantity" | "received_quantity">,
): number {
  return Math.max(0, row.quantity - row.received_quantity);
}

/** Who sent it and how: "PDQ Print" with "DPD 15501 2384 7801, sent 12 Oct" under it; the name alone when no despatch
 *  is recorded. */
export function goodsInSender(
  row: Pick<
    SupplierGoodsInRow,
    | "from_supplier_name"
    | "carrier"
    | "carrier_name"
    | "tracking_number"
    | "despatched_on"
  >,
): { name: string; detail: string | null } {
  const carrier =
    row.carrier === null
      ? null
      : row.carrier === "other"
        ? (row.carrier_name ?? carrierLabel("other"))
        : carrierLabel(row.carrier);
  const parts = [
    [carrier, row.tracking_number].filter(Boolean).join(" "),
    row.despatched_on ? `sent ${dayMonth(row.despatched_on)}` : "",
  ].filter((part) => part !== "");
  return {
    name: row.from_supplier_name,
    detail: parts.length > 0 ? parts.join(", ") : null,
  };
}

/** Why one receipt was refused, as a sentence. Trouble with the order is staff's to sort out and they are told at once. */
const GOODS_IN_REFUSAL: Record<GoodsRefusalReason, string> = {
  not_found: "We can't find this delivery at your warehouse.",
  more_than_due:
    "That's more than is still due for this delivery. Check the quantity.",
  line_cancelled:
    "Pollards cancelled this delivery. Please don't book it in; set the goods aside and tell your Pollards contact.",
  ref_used_elsewhere:
    "That reference is already used for a different delivery of this item. Use a new one.",
  not_recorded:
    "We couldn't record this one. Please try again, or tell your Pollards contact.",
};

export function goodsInRefusalSentence(
  reason: GoodsRefusalReason | null,
): string {
  return reason ? GOODS_IN_REFUSAL[reason] : GOODS_IN_REFUSAL.not_recorded;
}

/** One result's label: "Recorded", "Already recorded", or the refusal's sentence. */
export function goodsInResultSentence(result: GoodsReceivedResult): string {
  if (result.outcome === "recorded") return "Recorded";
  if (result.outcome === "already_recorded") return "Already recorded";
  return goodsInRefusalSentence(result.reason_code);
}

/** "2 recorded, 1 already recorded, 1 refused" for the banner over a bulk result. */
export function goodsInResultsSummary(results: GoodsReceivedResult[]): string {
  const of = (outcome: GoodsReceivedResult["outcome"]) =>
    results.filter((r) => r.outcome === outcome).length;
  const tallies: [number, string][] = [
    [of("recorded"), "recorded"],
    [of("already_recorded"), "already recorded"],
    [of("refused"), "refused"],
  ];
  return tallies
    .filter(([n]) => n !== 0)
    .map(([n, what]) => `${count(n)} ${what}`)
    .join(", ");
}

export const GOODS_IN_WORDING = {
  searchPlaceholder: "Search reference, item or order",
  warehouse: "Warehouse",
  empty: (tab: SupplierGoodsInTab, warehouseName: string | null) => {
    const at = warehouseName ? ` at ${warehouseName}` : "";
    switch (tab) {
      case "due":
        return `Nothing is due in${at}. New deliveries from Pollards' suppliers appear here.`;
      case "received_today":
        return `Nothing has been received today${at}.`;
      case "all":
        return `No deliveries${at} yet.`;
    }
  },
  noMatch: (q: string) => `No deliveries match "${q}".`,
  noWarehouses: "There is no goods in for your account",
  noWarehousesWhy:
    "Your supplier doesn't operate a warehouse for Pollards. If it should, ask your Pollards contact.",
  sortLabel: "Sort",
  sortEarliest: "Earliest expected first",
  sortLatest: "Latest expected first",
  sortOldest: "Oldest order first",
  selectRow: (lineRef: string) => `Select ${lineRef}`,
  selectedCount: (n: number) => `${count(n)} selected`,
  bulkTitle: "Record received for the selected deliveries",
  bulkHint:
    "One reference and date for them all, as for one delivery note. Each delivery is recorded on its own, so one being refused doesn't stop the rest. Give a delivery its own reference if the same item arrives on two.",
  quantityFor: (lineRef: string) => `Quantity received for ${lineRef}`,
  goodsInRef: "Goods-in reference",
  goodsInRefHint: "Your delivery note or goods-in reference.",
  receivedOn: "Received on",
  resultsTitle: "Result",
  columnReference: "Our reference",
  columnItem: "Item",
  columnFrom: "From",
  columnExpected: "Expected",
  columnQuantity: "Quantity",
  columnReceiving: "Receiving now",
  unknownDate: "Not known yet",
  unknownDelivery: "Unknown delivery",
} as const;

// ── Signing in, and the half of the portal that is not open yet ──

const SIGN_IN_ERROR: Record<string, string> = {
  no_access:
    "This email doesn't have a Pollards supplier portal login. Ask your Pollards contact to add you.",
  denied: "Sign-in was cancelled or refused. Please try again.",
  invalid_state:
    "That sign-in took too long, or was started in another browser. Please try again.",
  token_exchange_failed: "We couldn't finish signing you in. Please try again.",
  invalid_token:
    "We couldn't check your sign-in. Please try again, and if it keeps happening tell your Pollards contact.",
};

/** The sentence for a `/sign-in?error=` code the supplier app sent the browser back with; null when there is none. */
export function signInErrorSentence(code: string | null): string | null {
  if (code === null || code === "") return null;
  return SIGN_IN_ERROR[code] ?? "We couldn't sign you in. Please try again.";
}
